/**
 * SumarPostsModal — single-screen flow for the user to register one of their
 * existing TikTok/Instagram posts so it starts accruing puntos.
 *
 * Replaces the older `AddExternalModal` that had two separate sub-screens
 * (URL form + TikTok picker). This one lays out both input methods on
 * a single screen so the user sees every option at a glance.
 *
 * Two input methods, in priority order:
 *   1. Pick from a list of the user's own TikTok videos (picker — default open)
 *   2. Paste a URL (fallback for private posts, Instagram, or anything the
 *      TikTok API doesn't return)
 *
 * IMPORTANT distinction (don't break this in copy):
 *   - Catálogo de videos (home page grid) = videos that BRANDS upload for
 *     users to amplify.
 *   - Mis publicaciones (this modal's domain) = posts the user already
 *     published on their own social accounts, which mention a brand → these
 *     accrue puntos based on their stats.
 *
 * This modal is opened from two places (home + mis-publicaciones), so it
 * lives as its own component instead of being inlined.
 */

import React from 'react';
import {
  Modal, Button, Input, Select, Spin, Tag, message, Divider, Typography,
} from 'antd';
import {
  Eye, Heart, MessageCircle, Video as VideoIcon, Link as LinkIcon,
  Sparkles,
} from 'lucide-react';
import { tokens } from '../theme/tokens';
import { SPONSOR_NAME } from '../config/brand';

const { Text } = Typography;
const API_BASE = process.env.REACT_APP_API_BASE_URL || '';

// ── Types ──────────────────────────────────────────────────────────────────

interface TiktokPickerVideo {
  id: string;
  share_url: string;
  cover_image_url: string;
  video_description: string;
  title: string;
  duration: number;
  view_count: number;
  like_count: number;
  comment_count: number;
  share_count: number;
  create_time: number;
  already_tracked: boolean;
}

export interface SocialAccountLite {
  provider: string;
  socialId: string;
  username: string;
}

// Minimal subset of PublishTask used by callers. Keeping it `any` from the
// fetch response avoids re-importing the full type here.
export type PublishTaskLike = {
  _id?: string;
  task_id?: string;
  platform?: string;
  social_id?: string;
  [k: string]: unknown;
};

interface Props {
  accounts: SocialAccountLite[];
  open: boolean;
  onClose: () => void;
  /** Optional callback for the parent to react when a new task is created
   *  (e.g. inserting it into a local list without re-fetching). */
  onAdded?: (task: PublishTaskLike) => void;
}

// ── Helpers ────────────────────────────────────────────────────────────────

const formatCount = (n: number | undefined): string => {
  const v = n ?? 0;
  if (v >= 1_000_000) return `${(v / 1_000_000).toFixed(1)}M`;
  if (v >= 1_000)     return `${(v / 1_000).toFixed(1)}K`;
  return String(v);
};

// ── Sub: each card in the picker grid ──────────────────────────────────────

const VideoPickerCard: React.FC<{
  video: TiktokPickerVideo;
  onPick: () => void;
  picking: boolean;
}> = ({ video, onPick, picking }) => {
  const disabled = video.already_tracked || picking;
  const caption  = (video.video_description || video.title || '').trim() || '(sin descripción)';
  const captionTruncated = caption.length > 80 ? `${caption.slice(0, 80)}…` : caption;

  return (
    <div
      onClick={disabled ? undefined : onPick}
      style={{
        display: 'flex',
        flexDirection: 'column',
        background: '#fff',
        border: `1px solid ${video.already_tracked ? 'rgba(0,0,0,0.06)' : 'rgba(0,0,0,0.10)'}`,
        borderRadius: 10,
        overflow: 'hidden',
        cursor: disabled ? 'default' : 'pointer',
        opacity: video.already_tracked ? 0.7 : 1,
        transition: 'transform 0.12s ease, box-shadow 0.12s ease',
      }}
      onMouseEnter={e => { if (!disabled) e.currentTarget.style.boxShadow = '0 6px 18px rgba(0,0,0,0.10)'; }}
      onMouseLeave={e => { e.currentTarget.style.boxShadow = 'none'; }}
    >
      {/* Thumbnail */}
      <div style={{ position: 'relative', aspectRatio: '9 / 16', background: '#000' }}>
        {video.cover_image_url ? (
          <img src={video.cover_image_url} alt='' style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        ) : (
          <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#666' }}>
            <VideoIcon size={28} />
          </div>
        )}

        {/* "Sumando puntos" overlay — replaces the older "Ya trackeado" wording.
            States in the user's language: this post is already participating in
            puntos, so re-adding it is a no-op. */}
        {video.already_tracked && (
          <div style={{
            position: 'absolute', inset: 0,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: 'rgba(0,0,0,0.45)',
          }}>
            <Tag
              icon={<Sparkles size={11} style={{ marginRight: 4, verticalAlign: -1 }} />}
              style={{
                fontSize: 11, fontWeight: 700, margin: 0, borderRadius: 6,
                background: tokens.colors.brand.gradient,
                color: 'white', border: 'none', padding: '2px 8px',
                display: 'inline-flex', alignItems: 'center',
              }}
            >
              Sumando puntos
            </Tag>
          </div>
        )}

        {picking && (
          <div style={{
            position: 'absolute', inset: 0,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: 'rgba(255,255,255,0.7)',
          }}>
            <Spin />
          </div>
        )}
      </div>

      {/* Body */}
      <div style={{ padding: '8px 10px', display: 'flex', flexDirection: 'column', gap: 6, minHeight: 64 }}>
        <Text style={{ fontSize: 11, lineHeight: 1.3, color: tokens.colors.primary.neutral[700], wordBreak: 'break-word' }}>
          {captionTruncated}
        </Text>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, fontSize: 11, color: tokens.colors.primary.neutral[500] }}>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}>
            <Eye size={11} />{formatCount(video.view_count)}
          </span>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}>
            <Heart size={11} />{formatCount(video.like_count)}
          </span>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}>
            <MessageCircle size={11} />{formatCount(video.comment_count)}
          </span>
        </div>
        {/* Affordance: makes it explicit that the card itself is the action.
            Hidden once tracked (the overlay tag says it all in that state). */}
        {!video.already_tracked && (
          <div style={{
            marginTop: 4,
            paddingTop: 6,
            borderTop: '1px dashed rgba(0,0,0,0.08)',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 4,
            fontSize: 10,
            fontWeight: 600,
            color: tokens.colors.brand.purple,
          }}>
            <Sparkles size={10} />
            Tocá para sumar puntos
          </div>
        )}
      </div>
    </div>
  );
};

// ── Main modal ─────────────────────────────────────────────────────────────

// ── Inner content (no Modal chrome) ────────────────────────────────────────
// Everything below is the actual flow. It's split out from the Modal wrapper
// so it can ALSO be embedded inline in a page (see HIDE_CATALOG on the home
// page), rendering exactly the modal's content without the dialog overlay.
// `open` here means "active": gates the videos fetch. Inline callers pass
// `open` constant-true and an onClose no-op.
export const SumarPostsContent: React.FC<Props> = ({ accounts, open, onClose, onAdded }) => {
  // Split connected accounts by the methods they support. Today only TikTok
  // has a picker (via /v2/video/list/). Instagram is URL-only until we wire
  // an Apify-based picker.
  const tiktokAccounts = React.useMemo(() => accounts.filter(a => a.provider === 'tiktok'), [accounts]);

  // ── Picker/URL account IDs are DERIVED, not stored in state ───────────────
  // We used to keep them in useState + sync them via an effect when the
  // `accounts` prop arrived asynchronously. That created an infinite render
  // loop under React 19 because the parent passes `accounts.map(...)` inline
  // (new array reference every parent render) → useMemo for `tiktokAccounts`
  // recomputed → effect deps invalidated → effect re-ran. Even with idempotent
  // guards, the combination with the carousel's VideoPreloadProvider
  // (`updatePreloadStatus` always creates a new state object) tipped React's
  // max-update-depth detector.
  //
  // Fix: keep an optional user-chosen override in state; otherwise fall back
  // to the first account at render time. The default automatically picks up
  // accounts that arrive after mount, no effect needed.
  const [pickerOverride, setPickerOverride] = React.useState<string | null>(null);
  const pickerSocialId = pickerOverride ?? tiktokAccounts[0]?.socialId ?? '';

  const [urlOverride, setUrlOverride] = React.useState<string | null>(null);
  const urlSocialId = urlOverride ?? accounts[0]?.socialId ?? '';

  const [videos,         setVideos]         = React.useState<TiktokPickerVideo[]>([]);
  const [fetchingVideos, setFetchingVideos] = React.useState(false);
  const [videosCursor,   setVideosCursor]   = React.useState(0);
  const [videosHasMore,  setVideosHasMore]  = React.useState(false);
  const [videosError,    setVideosError]    = React.useState<string | null>(null);
  const [pickingVideoId, setPickingVideoId] = React.useState<string | null>(null);

  // URL input
  const [url,           setUrl]           = React.useState('');
  const [submittingUrl, setSubmittingUrl] = React.useState(false);

  // Fetch the picker's first page whenever the modal opens or the user
  // switches the TikTok account.
  React.useEffect(() => {
    if (!open || !pickerSocialId) return;
    setVideos([]);
    setVideosCursor(0);
    setVideosHasMore(false);
    setVideosError(null);
    void fetchVideosPage(pickerSocialId, 0, /* append */ false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, pickerSocialId]);

  const fetchVideosPage = async (sid: string, cursor: number, append: boolean) => {
    if (!sid) return;
    setFetchingVideos(true);
    setVideosError(null);
    try {
      const res = await fetch(`${API_BASE}/drupi/tiktok/${sid}/videos?cursor=${cursor}&max_count=20`);
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `HTTP ${res.status}`);
      }
      const data: { videos: TiktokPickerVideo[]; cursor: number; has_more: boolean } = await res.json();
      setVideos(prev => append ? [...prev, ...data.videos] : data.videos);
      setVideosCursor(data.cursor);
      setVideosHasMore(data.has_more);
    } catch (e: unknown) {
      setVideosError((e as Error).message || 'No pudimos cargar tus videos');
    } finally {
      setFetchingVideos(false);
    }
  };

  // Tap-to-pick used to fire the network call immediately, which surprised
  // users (no affordance that "tap = will track this post"). Now we open a
  // confirmation modal with a mini-preview + explicit copy. The actual
  // network request only fires on the modal's OK action.
  const submitPickedVideo = async (video: TiktokPickerVideo) => {
    setPickingVideoId(video.id);
    try {
      const captionFromList = (video.video_description || video.title || '').trim();
      const res = await fetch(`${API_BASE}/drupi/publish-tasks/external`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          url:           video.share_url,
          social_id:     pickerSocialId,
          caption:       captionFromList,
          thumbnail_url: video.cover_image_url || '',
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        message.error(err.detail || 'No pudimos sumar tu post');
        return;
      }
      const data = await res.json();
      if (onAdded) {
        const taskRes = await fetch(`${API_BASE}/drupi/publish-tasks/${data.task_id}`);
        if (taskRes.ok) onAdded(await taskRes.json());
      }
      message.success('Listo, tu post está sumando puntos');
      // Grey it out locally without re-fetching.
      setVideos(prev => prev.map(v => v.id === video.id ? { ...v, already_tracked: true } : v));
    } catch {
      message.error('Hubo un problema de red');
    } finally {
      setPickingVideoId(null);
    }
  };

  const handlePickVideo = (video: TiktokPickerVideo) => {
    if (video.already_tracked) return;
    const caption  = (video.video_description || video.title || '').trim() || '(sin descripción)';
    const captionShort = caption.length > 110 ? `${caption.slice(0, 110)}…` : caption;
    Modal.confirm({
      title: '¿Empezar a ganar puntos por este post?',
      icon: null,
      width: 460,
      okText: 'Sí, sumar puntos',
      cancelText: 'Cancelar',
      okButtonProps: {
        style: { background: tokens.colors.brand.gradient, border: 'none', fontWeight: 600 },
      },
      content: (
        <div style={{ display: 'flex', gap: 14, marginTop: 12 }}>
          {/* Mini-preview so the user sees WHICH video they're committing to */}
          <div style={{
            width: 64, height: 114, borderRadius: 8, overflow: 'hidden',
            background: '#000', flexShrink: 0,
          }}>
            {video.cover_image_url && (
              <img
                src={video.cover_image_url}
                alt=''
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              />
            )}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 0 }}>
            <Text style={{ fontSize: 12, color: tokens.colors.primary.neutral[700], lineHeight: 1.4, wordBreak: 'break-word' }}>
              {captionShort}
            </Text>
            <Text style={{ fontSize: 12, color: tokens.colors.primary.neutral[500], lineHeight: 1.5 }}>
              Vas a empezar a ganar puntos por las vistas de este post.
            </Text>
          </div>
        </div>
      ),
      onOk: () => submitPickedVideo(video),
    });
  };

  const handleAddByUrl = async () => {
    if (!url.trim()) return;
    setSubmittingUrl(true);
    try {
      const res = await fetch(`${API_BASE}/drupi/publish-tasks/external`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ url: url.trim(), social_id: urlSocialId }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        message.error(err.detail || 'No pudimos sumar tu post');
        return;
      }
      const data = await res.json();
      if (onAdded) {
        const taskRes = await fetch(`${API_BASE}/drupi/publish-tasks/${data.task_id}`);
        if (taskRes.ok) onAdded(await taskRes.json());
      }
      message.success('Listo, tu post está sumando puntos');
      setUrl('');
      onClose();
    } catch {
      message.error('Hubo un problema de red');
    } finally {
      setSubmittingUrl(false);
    }
  };

  // ── Render ──────────────────────────────────────────────────────────────

  return (
    <>
      {/* Header — explains what this screen does and, crucially, what it is
          NOT (uploading to the brand catalog). */}
      <div style={{ padding: '4px 16px 16px' }}>
        <div style={{
          fontSize: 20, fontWeight: 700, color: tokens.colors.primary.neutral[900],
          lineHeight: 1.2, marginBottom: 6,
        }}>
          Sumá puntos por tus posts
        </div>
        <Text style={{ color: tokens.colors.primary.neutral[600], fontSize: 13, lineHeight: 1.5, display: 'block' }}>
          Si ya publicaste algo en tus redes mencionando a {SPONSOR_NAME}, sumalo acá y empezás a ganar puntos por sus vistas, likes y compartidos.
        </Text>
      </div>

      {/* ─── Sección 1: Picker de tus videos ──────────────────────────────── */}
      <div style={{ padding: '0 16px 8px' }}>
        <SectionHeader
          icon={<VideoIcon size={14} />}
          title={tiktokAccounts.length === 0 ? 'Tus videos' : 'Tus videos de TikTok'}
          subtitle={
            tiktokAccounts.length === 0
              ? 'Cuando conectes una cuenta vas a poder elegir tus videos directo desde acá.'
              : 'Tocá uno para empezar a sumar puntos por él.'
          }
        />

        {/* Account switcher — only if the user has more than one TikTok */}
        {tiktokAccounts.length > 1 && (
          <div style={{ marginBottom: 12 }}>
            <Select
              value={pickerSocialId}
              onChange={setPickerOverride}
              style={{ width: '100%', maxWidth: 280 }}
              options={tiktokAccounts.map(a => ({ value: a.socialId, label: `@${a.username}` }))}
            />
          </div>
        )}

        {/* Picker body — error / loading / empty / grid */}
        {tiktokAccounts.length === 0 ? (
          <EmptyHint
            text='No tenés ninguna cuenta TikTok conectada todavía. Andá a Conexiones para vincularla, o pegá la URL de un post acá abajo.'
          />
        ) : videosError ? (
          <ErrorHint text={videosError} />
        ) : fetchingVideos && videos.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 40 }}><Spin /></div>
        ) : videos.length === 0 ? (
          <EmptyHint
            text='No encontramos videos públicos en esta cuenta. La API de TikTok solo nos deja listar los públicos — si tu post está en "Solo yo", cambialo a Público desde la app de TikTok, o pegá la URL acá abajo.'
          />
        ) : (
          <>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10 }}>
              {videos.map(v => (
                <VideoPickerCard
                  key={v.id}
                  video={v}
                  onPick={() => handlePickVideo(v)}
                  picking={pickingVideoId === v.id}
                />
              ))}
            </div>
            {videosHasMore && (
              <div style={{ textAlign: 'center', marginTop: 14 }}>
                <Button onClick={() => fetchVideosPage(pickerSocialId, videosCursor, true)} loading={fetchingVideos}>
                  Ver más videos
                </Button>
              </div>
            )}
          </>
        )}
      </div>

      {/* ─── Sección 2: URL ────────────────────────────────────────────────── */}
      <Divider style={{ margin: '20px 0 16px', color: tokens.colors.primary.neutral[400], fontSize: 12 }}>
        o pegá el link
      </Divider>

      <div style={{ padding: '0 16px' }}>
        <SectionHeader
          icon={<LinkIcon size={14} />}
          title='Sumá un post pegando su URL'
          subtitle='Para posts que no aparecen arriba (por ejemplo, privados).'
        />

        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <Input
            placeholder='https://www.tiktok.com/@user/video/...'
            value={url}
            onChange={e => setUrl(e.target.value)}
            onPressEnter={handleAddByUrl}
            prefix={<LinkIcon size={14} color={tokens.colors.primary.neutral[400]} />}
            allowClear
          />
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
            <div style={{ flex: '1 1 220px', minWidth: 0 }}>
              <Select
                value={urlSocialId}
                onChange={setUrlOverride}
                style={{ width: '100%' }}
                placeholder='En qué cuenta lo publicaste'
                options={accounts.map(a => ({ value: a.socialId, label: `@${a.username} · ${a.provider}` }))}
              />
            </div>
            {(() => {
              // When the gradient background is forced via inline style and
              // antd applies its disabled overlay, the result is a muddy
              // purple-grey with hard-to-read text. Drop the inline gradient
              // when disabled so antd's clean default disabled style (light
              // grey) takes over.
              const disabled = !url.trim() || !urlSocialId;
              return (
                <Button
                  type='primary'
                  onClick={handleAddByUrl}
                  loading={submittingUrl}
                  disabled={disabled}
                  style={
                    disabled
                      ? { fontWeight: 600 }
                      : { background: tokens.colors.brand.gradient, border: 'none', fontWeight: 600 }
                  }
                >
                  Sumar este post
                </Button>
              );
            })()}
          </div>
        </div>
      </div>
    </>
  );
};

// ── Modal wrapper ──────────────────────────────────────────────────────────
// Thin shell around SumarPostsContent. Existing callers (home button +
// mis-publicaciones) keep using this exactly as before — same props, same
// dialog. The inline (HIDE_CATALOG) path renders SumarPostsContent directly.
export const SumarPostsModal: React.FC<Props> = (props) => (
  <Modal
    open={props.open}
    onCancel={props.onClose}
    footer={null}
    width={780}
    title={null}
    style={{ top: 24 }}
    bodyStyle={{ padding: '8px 4px 0' }}
  >
    <SumarPostsContent {...props} />
  </Modal>
);

// ── Tiny helpers used inside the modal ─────────────────────────────────────

const SectionHeader: React.FC<{
  icon: React.ReactNode;
  title: React.ReactNode;
  subtitle?: string;
}> = ({ icon, title, subtitle }) => (
  <div style={{ marginBottom: 10 }}>
    <div style={{
      display: 'inline-flex', alignItems: 'center', gap: 7,
      color: tokens.colors.primary.neutral[800], fontWeight: 600, fontSize: 14,
    }}>
      <span style={{ display: 'inline-flex', color: tokens.colors.brand.blue }}>{icon}</span>
      {title}
    </div>
    {subtitle && (
      <div style={{ marginTop: 2, color: tokens.colors.primary.neutral[500], fontSize: 12, lineHeight: 1.45 }}>
        {subtitle}
      </div>
    )}
  </div>
);

const EmptyHint: React.FC<{ text: string }> = ({ text }) => (
  <div style={{
    padding: '14px 16px', borderRadius: 8,
    background: '#f9fafb', border: '1px dashed rgba(0,0,0,0.1)',
    color: tokens.colors.primary.neutral[600], fontSize: 13, lineHeight: 1.5,
  }}>
    {text}
  </div>
);

const ErrorHint: React.FC<{ text: string }> = ({ text }) => (
  <div style={{
    padding: '12px 14px', borderRadius: 8,
    background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)',
    color: '#dc2626', fontSize: 13, lineHeight: 1.5,
  }}>
    {text}
  </div>
);
